import React, { useEffect, useState } from "react";
import { compose, withAppContext, withState, withActions } from "containers/HOC";
import * as utils from "common/utils";
import Panel from "components/Panel/Panel";
import PanelBody from "components/Panel/PanelBody";
import ActionLink from "components/ActionLink";
import { confirm } from "containers/App/_actions";
import { withdrawFarmAccessRequest, resendFarmAccessRequest, getMyFarmAccessRequests } from "./_actions";
import Alert from "components/Alert";
import { Link } from "react-router-dom";
import Page from "components/Page";
import PageBody from "components/PageBody";
import DashboardPageHead from "./DashboardPageHead";

const MyPendingFarmAccessRequests = (props) => {
    const { getMyFarmAccessRequests, online, myFarmAccessRequestsLoading, myPendingFarmAccessRequests, confirm, resendFarmAccessRequest, withdrawFarmAccessRequest } = props;
    const [refreshCount, setRefreshCount] = useState(0);

    useEffect(() => {
        if (refreshCount > 0 && refreshCount < 10) {
            setTimeout(() => {
                getMyFarmAccessRequests();
                setRefreshCount(refreshCount + 1);
            }, 5000);
        }
        return () => {
            setRefreshCount(10);
        };
    }, [getMyFarmAccessRequests, refreshCount]);

    const resendAccessRequest = (ar) => {
        setRefreshCount(1);
        resendFarmAccessRequest(ar);
    };

    if (!myPendingFarmAccessRequests || myPendingFarmAccessRequests.length === 0) return null;

    const resend = (accessRequest) => (e) => {
        confirm(`Are you sure you want to resend your request to access,'${accessRequest.farmName}'?`, () => resendAccessRequest(accessRequest));
    };

    const withdraw = (accessRequest) => (e) => {
        confirm(`Are you sure you want to withdraw your request to access, '${accessRequest.farmName}'?`, () => withdrawFarmAccessRequest(accessRequest));
    };

    return (
        <Page>
            <DashboardPageHead breadcrumb={[{ name: "Requests" }]} />
            <PageBody>
                <Panel title={`My pending farm access requests (${myPendingFarmAccessRequests.length})`} iconClassName="IconLink--clock" loading={online && myFarmAccessRequestsLoading && refreshCount === 0} referrer={"/"} stickyHeaderName="dashboard.farmaccessrequests.collapsed">
                    <Alert type="info" text="The list of farms that you have requested access to and the status of the emails that have been sent to the farm owner(s)" />
                    <PanelBody>
                        <div className="Table">
                            <table>
                                <thead>
                                    <tr>
                                        <th data-width="20">Farm</th>
                                        <th data-width="15">Email Status</th>
                                        <th data-width="10">Requested access</th>
                                        <th data-width="10">Requested on</th>
                                        <th data-width="25">Comments</th>
                                        {online && <th className="th--shrink"></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {myPendingFarmAccessRequests
                                        .sort((a, b) => (b.farmName < a.farmName ? 1 : -1))
                                        .map((accessRequest, i) => {
                                            const isUnknown = accessRequest.emailStatus === "Unknown";
                                            return (
                                                <tr key={accessRequest.id} id={accessRequest.id} className={accessRequest.failed && !isUnknown ? "u-font-red u-font-600 hover" : "hover"}>
                                                    <td valign="top" id={`farmname_${i}`}>
                                                        <Link to={`/app/farm/${accessRequest.farmId}`} className="u-link u-textBold">
                                                            {accessRequest.farmName}
                                                        </Link>
                                                        <br />
                                                        {accessRequest.farmAddress}
                                                    </td>
                                                    <td valign="top" id={`status_${i}`}>
                                                        {(accessRequest.emailStatus || "").split(",").map((s) => (
                                                            <div key={s} className={s.includes("failed") && !s.includes(": 0") && !isUnknown ? "u-font-red" : ""}>
                                                                {s}
                                                            </div>
                                                        ))}
                                                    </td>
                                                    <td valign="top" id={`role_${i}`}>
                                                        {accessRequest.role}
                                                    </td>
                                                    <td valign="top" id={`date_${i}`}>
                                                        {utils.dateLong(accessRequest.requestedOn)}
                                                    </td>
                                                    <td valign="top" id={`comments_${i}`}>
                                                        {accessRequest.comments}
                                                    </td>
                                                    {online && (
                                                        <td>
                                                            <div className="u-flex">
                                                                <ActionLink id={`delete-access-request-${accessRequest.id}`} className="IconLink--edit u-ml-sm" onClick={resend(accessRequest)}>
                                                                    Resend
                                                                </ActionLink>
                                                                <ActionLink id={`delete-access-request-${accessRequest.id}`} className="IconLink--cross-circle u-ml-sm" onClick={withdraw(accessRequest)}>
                                                                    Withdraw
                                                                </ActionLink>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </PanelBody>
                </Panel>
            </PageBody>
        </Page>
    );
};

export default compose(
    withAppContext,
    withState((state, ownProps) => {
        const { authContext } = ownProps;

        const myPendingFarmAccessRequests = (state.dashboard.myFarmAccessRequests || []).filter((ar) => ar.status === "Pending" && ar.requestedBy.userId === authContext.userId);

        return {
            myPendingFarmAccessRequests,
            myFarmAccessRequestsLoading: state.dashboard.myFarmAccessRequestsLoading,
        };
    }),
    withActions({ confirm, withdrawFarmAccessRequest, resendFarmAccessRequest, getMyFarmAccessRequests })
)(MyPendingFarmAccessRequests);
